import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BellRing, ShieldCheck, Settings, GraduationCap } from 'lucide-react';
import {
  isNative,
  checkNotificationsPermission,
  requestNotificationsPermission,
  openNotificationSettings,
} from '../lib/mobileBridge';

const GOLD = '#C9A24E';

/** Jun 2026 — Mandatory notifications gate (Android app only).
 * Blocks the entire authenticated UI behind a persistent modal until
 * notification permission is granted, so late-class ringing reaches
 * every student. Re-checks on resume + every 3s while blocked. */
export const NotificationPermissionGate = () => {
  const [state, setState] = useState('granted'); // granted|prompt|denied
  const [manualHelp, setManualHelp] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    const { state: s } = await checkNotificationsPermission();
    if (s === 'web' || s === 'granted') {
      setState('granted');
      return true;
    }
    setState(s);
    return false;
  }, []);

  useEffect(() => {
    if (!isNative()) return undefined;
    refresh();
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    // Capacitor-native resume signal — fires the instant the user comes
    // back from system settings (more reliable than visibilitychange
    // inside the Android WebView).
    let appListener = null;
    const AppPlugin = window.Capacitor?.Plugins?.App;
    if (AppPlugin?.addListener) {
      try {
        appListener = AppPlugin.addListener('appStateChange', ({ isActive }) => {
          if (isActive) refresh();
        });
      } catch (_e) { /* old bridge */ }
    }
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      try {
        if (appListener?.remove) appListener.remove();
        else if (appListener?.then) appListener.then((h) => h?.remove && h.remove());
      } catch (_e) { /* noop */ }
    };
  }, [refresh]);

  // While blocked, poll so the moment the user flips the switch in
  // system settings and comes back, the gate lifts by itself.
  useEffect(() => {
    if (state === 'granted') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    pollRef.current = setInterval(async () => {
      const ok = await refresh();
      if (ok) {
        try {
          const bridge = await import('../lib/mobileBridge');
          bridge.registerPushToken({ force: true });
        } catch (_e) { /* best-effort re-register */ }
      }
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [state, refresh]);

  const activate = async () => {
    // Tiered flow that works across APK versions & Android releases:
    // 1) Ask the OS dialog first — on Android 13+ this still shows as
    //    long as the permission is not PERMANENTLY denied.
    const { state: s } = await requestNotificationsPermission();
    if (s === 'granted') { refresh(); return; }
    setState('denied');
    // 2) Deep-link straight into MESP's App Notification Settings
    //    (native method, APK v1.2.0+; validated on Android 8→14 via
    //    ACTION_APP_NOTIFICATION_SETTINGS + EXTRA_APP_PACKAGE, with an
    //    APPLICATION_DETAILS_SETTINGS fallback inside the Kotlin side).
    const opened = await openNotificationSettings();
    // 3) Old APKs without the native method → clear manual steps.
    //    The appStateChange + 3s poll auto-hides the gate the moment
    //    the user enables notifications and returns.
    if (!opened) setManualHelp(true);
  };

  if (state === 'granted') return null;

  return (
    <div
      dir="rtl"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-5"
      style={{ background: 'rgba(4, 9, 26, 0.92)', backdropFilter: 'blur(8px)' }}
      data-testid="notification-permission-gate"
    >
      <div className="w-full max-w-md rounded-3xl overflow-hidden text-center"
           style={{
             background: 'linear-gradient(150deg, #0A1128 0%, #131F42 100%)',
             border: '1px solid rgba(201,162,78,0.4)',
             boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
           }}>
        <div className="pt-8 pb-2 flex justify-center">
          <div className="relative w-20 h-20 rounded-full flex items-center justify-center"
               style={{ background: 'rgba(201,162,78,0.12)', border: '1.5px solid rgba(201,162,78,0.5)' }}>
            <BellRing size={34} style={{ color: GOLD }} className="animate-pulse" />
          </div>
        </div>

        <div className="px-6 pb-7 pt-3">
          <h2 className="text-lg font-extrabold text-white" data-testid="notification-gate-title">
            التنبيهات ركيزة أساسية في رحلتك الأكاديمية
          </h2>
          <p className="text-[13px] leading-relaxed text-white/70 mt-3">
            حرصاً على انتظامك وتفوقك، تعتمد المنصة على التنبيهات الفورية لإبلاغك
            بمواعيد حصصك المباشرة، ونداء المعلم عند بدء الدرس، والتعليمات الأكاديمية
            العاجلة. <span className="text-white/90 font-semibold">تفعيل الإشعارات شرط
            أساسي لاستخدام خدمات المنصة وإثبات الحضور</span> — حتى لا تفوتك أي حصة.
          </p>

          <div className="flex items-center justify-center gap-2 mt-4 text-[11px] text-white/50">
            <ShieldCheck size={13} style={{ color: GOLD }} />
            نرسل لك ما يخدم مسيرتك التعليمية فقط — دون أي إزعاج غير ضروري
          </div>

          <button
            type="button"
            onClick={activate}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl text-[15px] font-extrabold transition-transform active:scale-95"
            style={{
              background: 'linear-gradient(135deg, #E5C158, #C99A2F)',
              color: '#0A1128',
              boxShadow: '0 6px 18px rgba(201,154,47,0.45)',
            }}
            data-testid="notification-gate-activate-btn"
          >
            {state === 'prompt' ? <BellRing size={17} /> : <Settings size={17} />}
            تفعيل الآن
          </button>

          {manualHelp && (
            <div className="mt-4 text-[11.5px] text-white/60 bg-white/5 border border-white/10 rounded-xl p-3 text-right leading-relaxed"
                 data-testid="notification-gate-manual-help">
              افتح <span className="text-white/90">إعدادات الهاتف ← التطبيقات ← MESP ← الإشعارات</span> وفعّل
              «السماح بالإشعارات»، ثم عُد إلى التطبيق وسيُفتح تلقائياً.
            </div>
          )}

          <div className="flex items-center justify-center gap-1.5 mt-5 text-[10.5px]" style={{ color: 'rgba(201,162,78,0.8)' }}>
            <GraduationCap size={12} />
            منصة MESP التعليمية — نجاحك يبدأ من حضورك
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPermissionGate;
